import { useMemo, useState } from 'react'
import { activeTodos, completedTodos, useStore } from '../store'
import type { Todo, TodoStatus } from '../types'
import { ContextMenu, type MenuState, menuPos } from './ContextMenu'
import { ChevronLeft, SidebarIcon, TrashIcon } from './icons'

/** Open = hollow, in-progress = half filled, done = filled with a check. */
function StatusDot({ status }: { status: TodoStatus }) {
  if (status === 'done') {
    return (
      <span className="todo-dot done" aria-label="Completed">
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="#fff"
             strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5 6.5 12 13 4.5" />
        </svg>
      </span>
    )
  }
  if (status === 'in_progress') {
    return (
      <span className="todo-dot progress" aria-label="In progress">
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
          {/* Half-filled: the universal "started but not finished" mark. */}
          <path d="M9 1.6a7.4 7.4 0 0 0 0 14.8Z" fill="currentColor" />
        </svg>
      </span>
    )
  }
  return <span className="todo-dot open" aria-label="Not started" />
}

function Row({ todo, onMenu }: { todo: Todo; onMenu: (e: React.MouseEvent) => void }) {
  const store = useStore()
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <div className={`todo-row ${todo.status}`} onContextMenu={onMenu}>
      <button
        className="todo-dot-btn"
        title={todo.status === 'open' ? 'Mark In Progress'
          : todo.status === 'in_progress' ? 'Mark Completed' : 'Mark Not Started'}
        onClick={() => store.cycleTodoStatus(todo.id)}
      >
        <StatusDot status={todo.status} />
      </button>

      <input
        className="todo-text"
        value={draft ?? todo.text}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null && draft !== todo.text) store.updateTodoText(todo.id, draft)
          setDraft(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur() }
        }}
      />

      {todo.status === 'in_progress' && <span className="todo-badge">In Progress</span>}

      <button className="todo-del" title="Delete" onClick={() => store.deleteTodo(todo.id)}>
        <TrashIcon size={14} />
      </button>
    </div>
  )
}

export function TodoView({ onBack, showSidebarToggle, onShowSidebar }: {
  onBack: () => void
  showSidebarToggle?: boolean
  onShowSidebar?: () => void
}) {
  const store = useStore()
  const active = useMemo(() => activeTodos(store), [store])
  const done = useMemo(() => completedTodos(store), [store])
  const [draft, setDraft] = useState('')
  const [menu, setMenu] = useState<MenuState | null>(null)

  const submit = () => {
    if (!draft.trim()) return
    store.addTodo(draft)
    setDraft('')
  }

  const menuFor = (todo: Todo): MenuState['items'] => [
    { label: 'Not Started', checked: todo.status === 'open', onClick: () => store.setTodoStatus(todo.id, 'open') },
    { label: 'In Progress', checked: todo.status === 'in_progress', onClick: () => store.setTodoStatus(todo.id, 'in_progress') },
    { label: 'Completed', checked: todo.status === 'done', onClick: () => store.setTodoStatus(todo.id, 'done') },
    { type: 'separator' },
    { label: 'Delete', danger: true, onClick: () => store.deleteTodo(todo.id) },
  ]

  const inProgress = active.filter((t) => t.status === 'in_progress').length

  return (
    <section className="pane-todo">
      <div className="toolbar">
        <button className="back-btn" onClick={onBack}><ChevronLeft size={16} />Folders</button>
        {showSidebarToggle && (
          <button className="tb-btn" title="Show Sidebar" onClick={onShowSidebar}><SidebarIcon /></button>
        )}
        <div className="tb-spacer" />
        {done.length > 0 && (
          <button
            className="tb-btn danger"
            title="Clear Completed"
            onClick={() => void store.ask({
              title: `Delete ${done.length} completed item${done.length > 1 ? 's' : ''}?`,
              confirmLabel: 'Delete', danger: true,
            }).then((ok) => { if (ok) store.clearCompletedTodos() })}
          >
            <TrashIcon />
          </button>
        )}
      </div>

      <div className="todo-scroll">
        <div className="todo-inner">
          <h1 className="todo-title">To Do</h1>
          <div className="todo-summary">
            {active.length} remaining{inProgress > 0 && ` · ${inProgress} in progress`}
            {done.length > 0 && ` · ${done.length} completed`}
          </div>

          <div className="todo-add">
            <span className="todo-dot open" />
            <input
              placeholder="Add a to-do"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
            />
          </div>

          {active.length === 0 && done.length === 0 && (
            <div className="todo-empty">Nothing to do yet. Add your first item above.</div>
          )}

          {active.map((t) => (
            <Row key={t.id} todo={t} onMenu={(e) => setMenu({ ...menuPos(e), items: menuFor(t) })} />
          ))}

          {done.length > 0 && (
            <>
              <div className="todo-divider" />
              <div className="todo-section">Completed<span className="todo-count">{done.length}</span></div>
              {done.map((t) => (
                <Row key={t.id} todo={t} onMenu={(e) => setMenu({ ...menuPos(e), items: menuFor(t) })} />
              ))}
            </>
          )}
        </div>
      </div>

      <ContextMenu state={menu} onClose={() => setMenu(null)} />
    </section>
  )
}
